import { get, head, defaultTo } from 'lodash';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION } from 'constants/common';

/*************************************************************/

/**
 * @type {class}
 * @desc OffCallDispositionNotes defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { OffCallDispositionNotesService } from 'services/offCallDispositionNotes';
 *
 * // create service instance and send/cancel request
 * const offCallDispositionNotesService = new OffCallDispositionNotesService();
 * const result = await offCallDispositionNotesService.fetchData( '<user-id>' ); // fetch data
 * offCallDispositionNotesService.cancel(); // cancel an active request
 */
export class OffCallDispositionNotesService extends HttpService {

    // make request and fetch data
	async fetchData( { url = {}, data = {} } = {} ) {
		try {

            // extract data
            const { memberId, patientId } = data;

			const response = await this.post( {
				host: CONFIG.host,
				path: url,
				data: {
					context: {
                        memberId,
                        operation: CRUD_OPERATION.READ,
                        patientId
					},
					data: {},
				},
			} );

			return {
				status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
				data: {
					items: get( response, 'body.component.data.state.items', [] ),
					actions: get( response, 'body.component.structure.actions', [] ),
					fields: get( response, 'body.component.structure.fields', [] ),
				},
			};
		} catch ( errResponse ) {
			return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
		}
	}

	// add or update title/Description item
	async addUpdateItem( { url = {}, data = {} } = {} ) {

        // extract `operation` and actual payload
        const { operation, id = null, title, description, memberId, patientId } = data;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        memberId,
                        operation,
                        patientId
                    },
                    data: {
                        id,
                        title,
                        description,
                    },
                }
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    id: defaultTo( head( get( response, 'body.component.data.itemIds', [] ) ), id ),
                    title,
                    description,
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // delete a title/Description item
    async deleteItem( { url = {}, data = {} } = {} ) {

        // extract `operation` and actual payload
        const { id, memberId, patientId } = data;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        memberId,
                        operation: CRUD_OPERATION.DELETE,
                        patientId
                    },
                    data: {
                        id
                    }
                }
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: null,
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // format error response
    _errorFormatter( errResponse ) {
        return errResponse; // some logic
    }
}
